import { Backdrop, Box, Button, Checkbox, FormControl, Grid, ListItemText, MenuItem, Select, Stack, Typography } from "@mui/material";
import AppLoader from '../../components/AppLoader/AppLoader';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from "highcharts/highcharts-more";
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DISCO_COMPANY_OPTIONS } from "../../components/cfcDashboard/DashboardCompanyToolbar";
import DashboardHeaderLoading from "../../components/cfcDashboard/DashboardHeaderLoading";
import { cfpBaseUrl } from "../../network/urls";
import {
    getInitialAdminOfficeCode,
    resolveReportGoOfficeCode,
} from "../../utils/adminCompany";
import {
    renderCircleValue,
    renderDivisionValue,
    renderSubDivisionValue,
} from "../Report/coaHierarchySelectRenderers";
import "./CfcDashboard.scss";

const CommercialServicesCard = lazy(() => import("../../components/commercialServicesCard/CommercialServicesCard"));
const DashboardHeader = lazy(() => import("../../components/cfcDashboard/DashboardHeader"));

HighchartsMore(Highcharts);

const useSidebarTheme = () => {
    const [theme, setTheme] = useState(
        () => document.documentElement.getAttribute("data-sidebar-theme") || "light",
    );

    useEffect(() => {
        const root = document.documentElement;
        const sync = () => setTheme(root.getAttribute("data-sidebar-theme") || "light");
        const observer = new MutationObserver(sync);
        observer.observe(root, { attributes: true, attributeFilter: ["data-sidebar-theme"] });
        return () => observer.disconnect();
    }, []);

    return theme;
};

const TechnicalOperations = () => {
    const sidebarTheme = useSidebarTheme();
    const isDark = sidebarTheme === "dark";
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const dispatch = useDispatch();

    const { token, code, name, roleName, roleTypeName, cscID, cfcLevelId, roleId, hierarchy, selectedAdminCompanyCode } = useSelector(state => state.auth)
    const isCodeTenAdmin = String(code) === "10";
    const [companyCode, setCompanyCode] = useState(
        getInitialAdminOfficeCode(code, selectedAdminCompanyCode) || "15",
    );
    const [selectedCompanyCode, setSelectedCompanyCode] = useState(
        getInitialAdminOfficeCode(code, selectedAdminCompanyCode) || "15",
    );
    const [circleCode, setCircleCode] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("");
    const [selectedSubDivision, setSelectedSubDivision] = useState("");
    const [appliedCompanyCode, setAppliedCompanyCode] = useState("");
    const [appliedCircleCode, setAppliedCircleCode] = useState("");
    const [appliedDivision, setAppliedDivision] = useState("");
    const [appliedSubDivision, setAppliedSubDivision] = useState("");
    const [initialAdminDataLoaded, setInitialAdminDataLoaded] = useState(false);
    const [hierarchyLoading, setHierarchyLoading] = useState(false);
    const [filteredCircles, setFilteredCircles] = useState([]);
    const [filteredDivisions, setFilteredDivisions] = useState([]);
    const [filteredSubDivisions, setFilteredSubDivisions] = useState([]);
    const [subDivisions, setSubDivisions] = useState(
        JSON.parse(window.localStorage.getItem("subDivisions")) || [],
    );
    const [desginationCodeToApi, setDesginationCodeToApi] = useState("113");
    const [desginationList, setDesginationList] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [disco, setDisco] = useState("15");
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [selectedDesignations, setSelectedDesignations] = useState([113]);
    const [selectedDesignationsForApi, setSelectedDesignationsForApi] = useState("113");
    const navigate = useNavigate();
    const handleChangeDesgination = (e) => {
        setDesginationCodeToApi(e.target.value)
    }
    const tableRef = useRef(null);

    const resolveOfficeCodeFromFilters = useCallback(
        (filters) =>
            resolveReportGoOfficeCode({
                code,
                officeCode: filters.companyCode,
                selectedCompanyCode: filters.companyCode,
                selectedAdminCompanyCode,
                circleCode: filters.circleCode,
                selectedDivision: filters.divisionCode,
                selectedSubDivision: filters.subDivisionCode,
            }),
        [code, selectedAdminCompanyCode],
    );

    const filtersAreEqual = (left, right) =>
        String(left.companyCode || "") === String(right.companyCode || "") &&
        String(left.circleCode || "") === String(right.circleCode || "") &&
        String(left.divisionCode || "") === String(right.divisionCode || "") &&
        String(left.subDivisionCode || "") === String(right.subDivisionCode || "");

    const draftFilters = {
        companyCode: selectedCompanyCode,
        circleCode: circleCode || "",
        divisionCode: selectedDivision || "",
        subDivisionCode: selectedSubDivision || "",
    };

    const appliedFilters = {
        companyCode: appliedCompanyCode,
        circleCode: appliedCircleCode || "",
        divisionCode: appliedDivision || "",
        subDivisionCode: appliedSubDivision || "",
    };

    const isFilterGoReady =
        isCodeTenAdmin &&
        initialAdminDataLoaded &&
        Boolean(selectedCompanyCode) &&
        !filtersAreEqual(draftFilters, appliedFilters) &&
        !viewBackdrop &&
        !hierarchyLoading;

    const getOfficeCodeForApi = useCallback(() => {
        if (isCodeTenAdmin) {
            return resolveOfficeCodeFromFilters({
                companyCode: appliedCompanyCode,
                circleCode: appliedCircleCode || "",
                divisionCode: appliedDivision || "",
                subDivisionCode: appliedSubDivision || "",
            });
        }

        if (hierarchy == 1) return disco;
        if (hierarchy == 2) return code.slice(0, 2);
        if (hierarchy == 3) return code.slice(0, 3);
        if (hierarchy == 4) return code.slice(0, 4);
        if (hierarchy == 52) return code;
        return "15";
    }, [
        isCodeTenAdmin,
        resolveOfficeCodeFromFilters,
        appliedCompanyCode,
        appliedCircleCode,
        appliedDivision,
        appliedSubDivision,
        hierarchy,
        disco,
        code,
    ]);

    const populateCirclesFromSubDivisions = (rows = []) => {
        const circles = rows.reduce((acc, curr) => {
            if (!acc.some((item) => item.circleCode === curr.circleCode)) {
                acc.push({
                    circleCode: curr.circleCode,
                    circleName: curr.circleName,
                    companyCode: curr.companyCode,
                });
            }
            return acc;
        }, []);
        setFilteredCircles(circles);
    };

    const fetchSubDivisionsForCompany = async (nextCompanyCode, { usePageBackdrop = false } = {}) => {
        if (!isCodeTenAdmin || !nextCompanyCode) {
            return [];
        }

        if (usePageBackdrop) {
            setViewBackdrop(true);
        } else {
            setHierarchyLoading(true);
        }
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        const formdata = new FormData();
        formdata.append("companyCode", nextCompanyCode);
        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow",
        };

        try {
            const response = await fetch(
                `${cfpBaseUrl}customerFaciliationPortal/subDivWithCompDetails`,
                requestOptions,
            );
            const result = await response.json();
            if (result.status === "200") {
                setSubDivisions(result.message);
                populateCirclesFromSubDivisions(result.message);
                return result.message;
            }
            setSubDivisions([]);
            setFilteredCircles([]);
            setFilteredDivisions([]);
            setFilteredSubDivisions([]);
            return [];
        } catch (error) {
            setSubDivisions([]);
            setFilteredCircles([]);
            setFilteredDivisions([]);
            setFilteredSubDivisions([]);
            return [];
        } finally {
            if (usePageBackdrop) {
                setViewBackdrop(false);
            } else {
                setHierarchyLoading(false);
            }
        }
    };

    const getDateDifference = (startDate, endDate) => {
        // Convert the date strings to Date objects
        const startDates = startDate.split(',')[0]
        const endDates = endDate.split(',')[0]

        const start = new Date(startDates);
        const end = new Date(endDates);

        // Calculate the difference in time (milliseconds)
        const differenceInTime = end - start;

        // Convert time difference from milliseconds to days
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);

        return differenceInDays;
    };

    const handleChange = (event) => {
        // setSelectedDesignations(event.target.value);
        const selectedValues = event.target.value; // e.g., [1135, 96, 98]
        // const selectedString = selectedValues.join(','); // e.g., "1135,96,98"
        setSelectedDesignations(event.target.value);
        const selectedString = selectedValues.join(',');
        setSelectedDesignationsForApi(selectedString);
    };
    const [complaintCharts, setComplaintCharts] = useState({
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            zooming: {
                type: 'xy'
            },
            panning: {
                enabled: true,
                type: 'xy'
            },
            panKey: 'shift'
        },
        title: {
            text: 'Technical Complaints'
        },
        tooltip: {
            valueSuffix: '%'
        },
        subtitle: {
            text: null
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    distance: -40,
                    format: '{point.percentage:.1f}%',
                    style: {
                        fontSize: '1.2em',
                        textOutline: 'none',
                        opacity: 0.7
                    },
                    filter: {
                        operator: '>',
                        property: 'percentage',
                        value: 10
                    }
                }
            }
        },
        credits: {
            enabled: false
        },
        series: [
            {

            }
        ]
    });

    const themedComplaintCharts = useMemo(() => ({
        ...complaintCharts,
        chart: {
            ...complaintCharts.chart,
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
        },
        title: {
            ...complaintCharts.title,
            style: {
                color: isDark ? '#e2e8f0' : '#1e293b',
                fontWeight: '600',
            },
        },
        legend: {
            itemStyle: {
                color: isDark ? '#cbd5e1' : '#334155',
            },
            itemHoverStyle: {
                color: isDark ? '#f8fafc' : '#0f172a',
            },
        },
        plotOptions: {
            ...complaintCharts.plotOptions,
            pie: {
                ...complaintCharts.plotOptions?.pie,
                dataLabels: {
                    ...complaintCharts.plotOptions?.pie?.dataLabels,
                    style: {
                        ...complaintCharts.plotOptions?.pie?.dataLabels?.style,
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        textOutline: isDark ? 'none' : 'none',
                    },
                },
            },
        },
    }), [complaintCharts, isDark]);
    const statusRouteMap = {
        Pending: "/power-complaint-pending",
        Allocated: "/power-complaint-allocated",
        Working: "/power-complaint-working",
        Onhold: "/power-complaint-onhold",
        Escalated: "/power-complaint-escalated",
        Resolved: "/power-complaint-resolved",
    };
    const [serviceCards, setServiceCards] = useState([

    ])



    const getCurrentDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };


    const getData = async (officeCodeOverride) => {
        try {
            setViewBackdrop(true);
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                officeCode: officeCodeOverride ?? getOfficeCodeForApi(),
                dateFrom: getCurrentDate(),
                dateTo: getCurrentDate()
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch(cfpBaseUrl + "lineComplaints/getComplaintCountStatusWiseHierarchical", requestOptions);
            const result = await response.json();

            if (result.status == 200) {
                const statusColorMap = {
                    Pending: "#FFCDD2",
                    Allocated: "#B2EBF2",
                    Working: "#C8E6C9",
                    Onhold: "#D1C4E9",
                    Escalated: "#FFF9C4",
                    Resolved: "#A5D6A7",
                    Total: "#FFE0B2"
                };

                // ✅ Required statuses for sum
                const requiredStatuses = ["Pending", "Allocated", "Working", "Onhold", "Resolved"];

                let cards = [];

                const statusSumMap = {
                    Pending: 0,
                    Allocated: 0,
                    Working: 0,
                    Onhold: 0,
                    Resolved: 0
                };

                result.data.forEach(item => {
                    const statusKey = item.Status?.toLowerCase();
                    const matchedKey = Object.keys(statusSumMap).find(
                        key => key.toLowerCase() === statusKey
                    );
                    if (matchedKey) {
                        statusSumMap[matchedKey] += item.TotalCount || 0;
                    }
                });

                // ✅ Create cards from summed values
                cards = Object.keys(statusSumMap).map(status => ({
                    title1: status,
                    title2: "Complaints",
                    yearlyCounter: statusSumMap[status],
                    color: statusColorMap[status],
                    onClick: () => navigate(statusRouteMap[status]),
                }));

                const totalCount = Object.values(statusSumMap).reduce((acc, curr) => acc + curr, 0);
                cards.push({
                    title1: "Total",
                    title2: "Complaints",
                    yearlyCounter: totalCount,
                    color: statusColorMap["Total"],
                    onClick: null,
                });

                // ✅ Update chart
                const updatedComplaintCharts = {
                    ...complaintCharts,
                    series: [
                        {
                            name: "Percentage",
                            colorByPoint: true,
                            data: cards
                                .filter(card => card.title1 !== "Total")
                                .map(card => ({
                                    name: card.title1,
                                    y: card.yearlyCounter,
                                    color: card.color
                                }))
                        }
                    ]
                };

                if (result.data && cards.length > 0) {
                    setComplaintCharts(updatedComplaintCharts);
                    setServiceCards(cards);
                    setIsDataLoaded(true);
                    setViewBackdrop(false);
                } else {
                    setServiceCards([]);
                    setComplaintCharts({
                        ...complaintCharts,
                        series: [{ name: "Percentage", colorByPoint: true, data: [] }]
                    });
                    setIsDataLoaded(false);
                    setViewBackdrop(false);
                }

                return cards;
            } else if (result.status == 400) {
                setServiceCards([]);
                setComplaintCharts({
                    ...complaintCharts,
                    series: [{ name: "Percentage", colorByPoint: true, data: [] }]
                });
                setIsDataLoaded(true);
                setViewBackdrop(false);
            } else if (result.status == 401) {
                // ... existing logout logic
            }

        } catch (error) {
            setServiceCards([]);
            setComplaintCharts({
                ...complaintCharts,
                series: [{ name: "Percentage", colorByPoint: true, data: [] }]
            });
            setIsDataLoaded(false);
            setViewBackdrop(false);
        }
    };


    useEffect(() => {
        if (!isCodeTenAdmin) {
            getData();
            return;
        }

        if (initialAdminDataLoaded) {
            return;
        }

        const company = String(
            selectedAdminCompanyCode ||
            getInitialAdminOfficeCode(code, selectedAdminCompanyCode) ||
            "15",
        );

        setSelectedCompanyCode(company);
        setCompanyCode(company);
        setDisco(company);
        setAppliedCompanyCode(company);
        setAppliedCircleCode("");
        setAppliedDivision("");
        setAppliedSubDivision("");

        const loadInitialAdminData = async () => {
            await fetchSubDivisionsForCompany(company, { usePageBackdrop: true });
            const officeCode = resolveOfficeCodeFromFilters({
                companyCode: company,
                circleCode: "",
                divisionCode: "",
                subDivisionCode: "",
            });
            await getData(officeCode);
            setInitialAdminDataLoaded(true);
        };

        loadInitialAdminData();
    }, [
        isCodeTenAdmin,
        selectedAdminCompanyCode,
        code,
        initialAdminDataLoaded,
        resolveOfficeCodeFromFilters,
    ]);

    const handleChangeCompany = async (event) => {
        const nextCompanyCode = event.target.value;
        setSelectedCompanyCode(nextCompanyCode);
        setCompanyCode(nextCompanyCode);
        setDisco(nextCompanyCode);
        setCircleCode("");
        setSelectedDivision("");
        setSelectedSubDivision("");
        setFilteredDivisions([]);
        setFilteredSubDivisions([]);
        await fetchSubDivisionsForCompany(nextCompanyCode);
    };

    const handleCircleChange = (event) => {
        const nextCircleCode = event.target.value ? parseInt(event.target.value, 10) : "";
        setCircleCode(nextCircleCode);
        setSelectedDivision("");
        setSelectedSubDivision("");
        setFilteredSubDivisions([]);

        if (!nextCircleCode) {
            setFilteredDivisions([]);
            return;
        }

        const uniqueDivisions = subDivisions
            .filter((item) => item.circleCode === nextCircleCode)
            .reduce((acc, item) => {
                if (!acc.some((division) => division.divisionCode === item.divisionCode)) {
                    acc.push({
                        divisionCode: item.divisionCode,
                        divisionName: item.divisionName,
                        companyCode: item.companyCode,
                    });
                }
                return acc;
            }, []);
        setFilteredDivisions(uniqueDivisions);
    };

    const handleDivisionChange = (event) => {
        const nextDivisionCode = event.target.value ? parseInt(event.target.value, 10) : "";
        setSelectedDivision(nextDivisionCode);
        setSelectedSubDivision("");

        if (!nextDivisionCode) {
            setFilteredSubDivisions([]);
            return;
        }

        const uniqueSubDivisions = subDivisions
            .filter((item) => item.divisionCode === nextDivisionCode)
            .reduce((acc, item) => {
                if (!acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)) {
                    acc.push({
                        subDivisionCode: item.subDivisionCode,
                        subDivisionName: item.subDivisionName,
                        companyCode: item.companyCode,
                    });
                }
                return acc;
            }, []);
        setFilteredSubDivisions(uniqueSubDivisions);
    };

    const handleSubDivisionChange = (event) => {
        const nextSubDivisionCode = event.target.value ? parseInt(event.target.value, 10) : "";
        setSelectedSubDivision(nextSubDivisionCode);
    };

    const handleFilterGo = async () => {
        if (!isFilterGoReady) {
            return;
        }

        const officeCode = resolveOfficeCodeFromFilters(draftFilters);
        setAppliedCompanyCode(selectedCompanyCode);
        setAppliedCircleCode(circleCode || "");
        setAppliedDivision(selectedDivision || "");
        setAppliedSubDivision(selectedSubDivision || "");
        await getData(officeCode);
        if (hierarchy == 52) {
            await getStats(officeCode);
        }
    };

    useEffect(() => {
        if (hierarchy == 52) {
            getStats()
        }
    }, [desginationCodeToApi])
    const connectionTypes = [
        'User Name',
        'Sub Division Name',
        'Role Name',
        // 'SDIV Count',
        'LoggedIn User',
        'LoggedOut User',
        // 'Total '
    ];


    const getDesgination = async () => {

        setViewBackdrop(true)
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const raw = "";

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        fetch(cfpBaseUrl + "workForce/getListOfSubDivisionDesignations", requestOptions)
            .then((response) => response.json())
            .then((result) => {

                if (result.message == 'Data Found.') {
                    const requiredDesignations = ["LM-I", "LM-II", "LS-I", "LS-II", "ALM"];

                    const filtered = result.data.filter(item =>
                        requiredDesignations.includes(item.employeeDesignationName?.toUpperCase())
                    );
                    setDesginationList(filtered)
                }
                // setViewBackdrop(false)
            })
            .catch(() => {});
    }
    useEffect(() => {
        if (hierarchy == 52) {
            getDesgination()
        }
    }, [])
    const getStats = async (officeCodeOverride) => {
        setViewBackdrop(true);
        try {
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                officeCode: officeCodeOverride ?? getOfficeCodeForApi(),
                roleId: selectedDesignationsForApi ? selectedDesignationsForApi : '113',
                isLoggedInFromMobile: '2',
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch(cfpBaseUrl + "workForce/getUserLoggedStatus", requestOptions);
            const result = await response.json();

            if (result.status == '200') {
                setViewBackdrop(false);
                setTableData(result.data)
            } else {
                setViewBackdrop(false);
                setTableData([])
            }
        } catch (error) {
            setTableData([]); // Clear tableData on error
        }
        // setViewBackdrop(false);
    };

    const updatedTableData = tableData.map((item) => {
        const isLoggedIn = item.isLogged_In === true;

        return {
            ...item,
            LoggedInLineStaff: isLoggedIn ? 1 : 0,
            NeverLoggedInLineStaff: !isLoggedIn ? 1 : 0,
            totalLineStaff: 1
        };
    });

    const handleConfirm = () => {
        if (hierarchy == 52) {
            getStats()
        }
    };

    return (
        <>

            <Box className="form-box">
                <div className="cfc-dashboard">
                    {isCodeTenAdmin ? (
                        <Box className="tech-ops-admin-filters">
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={2}>
                                    <Stack spacing={0.5}>
                                        <Typography className="field-label">Company</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={selectedCompanyCode || ""}
                                                onChange={handleChangeCompany}
                                                displayEmpty
                                            >
                                                {DISCO_COMPANY_OPTIONS.map((company) => (
                                                    <MenuItem key={company.code} value={company.code}>
                                                        {company.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                {isCodeTenAdmin ? (
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Stack spacing={0.5}>
                                            <Typography className="field-label">Circle</Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={circleCode || ""}
                                                    onChange={handleCircleChange}
                                                    displayEmpty
                                                    renderValue={(value) =>
                                                        renderCircleValue(value, filteredCircles)
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        <em>All Circle</em>
                                                    </MenuItem>
                                                    {filteredCircles.map((circle) => (
                                                        <MenuItem
                                                            key={circle.circleCode}
                                                            value={circle.circleCode}
                                                        >
                                                            {circle.circleName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                ) : null}
                                {isCodeTenAdmin ? (
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Stack spacing={0.5}>
                                            <Typography className="field-label">Division</Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={selectedDivision || ""}
                                                    onChange={handleDivisionChange}
                                                    displayEmpty
                                                    disabled={!circleCode}
                                                    renderValue={(value) =>
                                                        renderDivisionValue(value, filteredDivisions)
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        <em>All Division</em>
                                                    </MenuItem>
                                                    {filteredDivisions.map((division) => (
                                                        <MenuItem
                                                            key={division.divisionCode}
                                                            value={division.divisionCode}
                                                        >
                                                            {division.divisionName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                ) : null}
                                {isCodeTenAdmin ? (
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Stack spacing={0.5}>
                                            <Typography className="field-label">Sub-Division</Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={selectedSubDivision || ""}
                                                    onChange={handleSubDivisionChange}
                                                    displayEmpty
                                                    disabled={!selectedDivision}
                                                    renderValue={(value) =>
                                                        renderSubDivisionValue(value, filteredSubDivisions)
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        <em>All Sub-Division</em>
                                                    </MenuItem>
                                                    {filteredSubDivisions.map((subDivision) => (
                                                        <MenuItem
                                                            key={subDivision.subDivisionCode}
                                                            value={subDivision.subDivisionCode}
                                                        >
                                                            {subDivision.subDivisionName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                ) : null}
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="contained"
                                        onClick={handleFilterGo}
                                        disabled={!isFilterGoReady}
                                        sx={{
                                            height: 40,
                                            minWidth: 88,
                                            backgroundColor: isFilterGoReady ? "#0b5cab" : "#cbd5e1",
                                            color: "#fff",
                                            boxShadow: "none",
                                            "&:hover": {
                                                backgroundColor: isFilterGoReady ? "#094d8f" : "#cbd5e1",
                                            },
                                            "&.Mui-disabled": {
                                                backgroundColor: "#cbd5e1",
                                                color: "#fff",
                                            },
                                        }}
                                    >
                                        Go
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : null}
                    <Suspense fallback={<DashboardHeaderLoading />}>
                        {isDataLoaded ? (
                            serviceCards && serviceCards.length > 0 ? (
                                <DashboardHeader
                                    title="Customer Services"
                                    serviceCards={serviceCards}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Typography variant="h6" style={{ color: '#999' }}>
                                        No Data Found
                                    </Typography>
                                </div>
                            )
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <AppLoader />
                                <Typography variant="h6" style={{ marginTop: 20, color: '#666' }}>
                                    Loading Data...
                                </Typography>
                            </div>
                        )}
                    </Suspense>
                </div>

                <div className="tech-ops-chart-wrap">
                    {isDataLoaded ? (
                        serviceCards && serviceCards.length > 0 ? (
                            <HighchartsReact highcharts={Highcharts} options={themedComplaintCharts} />
                        ) : (
                            <div className="tech-ops-chart-empty">
                                <Typography variant="h5" className="tech-ops-muted-text">
                                    No Data Found
                                </Typography>
                            </div>
                        )
                    ) : (
                        <div className="tech-ops-chart-empty">
                            <Typography className="tech-ops-muted-text">
                                Loading chart...
                            </Typography>
                        </div>
                    )}
                </div>
            </Box>

            {/* Second Box added vertically below */}
            {
                hierarchy == 52 && (
                    <Box className="form-box tech-ops-lineman">
                        <h3 className="tech-ops-lineman__title">Lineman App Usage Summary</h3>
                        <div className='div-datagrid' >
                            <div className='reports-container' >
                                <div className='filter-fields' style={{ width: '75%', justifyContent: 'flex-start', gap: '15px' }}>

                                    <div style={{ display: 'flex', marginTop: 20 }}>
                                        <div style={{ display: 'flex', marginTop: 20, flexDirection: 'column' }}>
                                            <p className="field-label tech-ops-lineman__label">Designation</p>
                                            <FormControl fullWidth>
                                                <Select
                                                    className="tech-ops-lineman__select"
                                                    labelId="designation-select-label"
                                                    multiple
                                                    value={selectedDesignations}
                                                    onChange={handleChange}
                                                    sx={{ height: 36, width: 300 }}
                                                    renderValue={(selected) =>
                                                        selected
                                                            .map((id) =>
                                                                desginationList.find((item) => item.employeeDesignationId === id)
                                                                    ?.employeeDesignationName || id
                                                            )
                                                            .join(', ')
                                                    }
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { width: 300 },
                                                        },
                                                    }}
                                                >
                                                    {desginationList.map((item) => (
                                                        <MenuItem key={item.employeeDesignationId} value={item.employeeDesignationId}>
                                                            <Checkbox checked={selectedDesignations.includes(item.employeeDesignationId)} />
                                                            <ListItemText primary={item.employeeDesignationName} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                        </div>
                                        <div style={{ marginLeft: 25, marginTop: 30 }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                style={{ marginTop: '10px' }}
                                                onClick={handleConfirm}
                                            >
                                                Go
                                            </Button>
                                        </div>

                                    </div>

                                </div>

                                <div className="export-button-container">

                                </div>
                            </div>
                            <div className="table-container" style={{ marginBottom: '40px' }}>
                                <div id="pdf-table">
                                    <div style={{ maxHeight: '900px', overflowY: 'auto' }}>
                                        <table
                                            className="table tech-ops-lineman__table"
                                            ref={tableRef}
                                        >
                                            <thead>
                                                <tr className="tech-ops-lineman__head-spacer">
                                                    <th colSpan="14" />
                                                </tr>
                                                <tr>
                                                    {connectionTypes.map((type, index) => (
                                                        <th
                                                            key={index}
                                                            style={{
                                                                width: `${100 / connectionTypes.length}%`,
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            {type}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {updatedTableData.length > 0 ? (
                                                    <>
                                                        {updatedTableData.map((item, id) => {
                                                            const role = desginationList.find(designation => designation.employeeDesignationId === item.role_id);
                                                            return (
                                                                <tr key={id}>
                                                                    <td style={{ textAlign: 'left' }}>{item.userName || '-'}</td>
                                                                    <td style={{ textAlign: 'left' }}>{item.subDivisionName || '-'}</td>
                                                                    <td style={{ textAlign: 'left' }}>{role?.employeeDesignationName || '-'}</td>
                                                                    <td style={{ textAlign: 'left' }}>{item.LoggedInLineStaff}</td>
                                                                    <td style={{ textAlign: 'left' }}>{item.NeverLoggedInLineStaff}</td>
                                                                </tr>
                                                            );
                                                        })}

                                                        <tr className="tech-ops-lineman__total-row">
                                                            <td colSpan={3} style={{ textAlign: 'left' }}>Total ({updatedTableData.reduce((sum, item) => sum + item.totalLineStaff, 0)})</td>
                                                            <td style={{ textAlign: 'left' }}>
                                                                {updatedTableData.reduce((sum, item) => sum + item.LoggedInLineStaff, 0)}
                                                            </td>
                                                            <td style={{ textAlign: 'left' }}>
                                                                {updatedTableData.reduce((sum, item) => sum + item.NeverLoggedInLineStaff, 0)}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="tech-ops-lineman__empty">
                                                            No Data Found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Box>
                )
            }



            < Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={viewBackdrop}
            >
                <AppLoader />
            </Backdrop >
            {/* <div style={{ background: 'red', width: '50%' }}>

            </div> */}
        </>
    );
};

export default TechnicalOperations;
